const initProductDetails = function (sectionId) {
  const root = document.querySelector(`[data-fs-product-details][data-section-id="${sectionId}"]`);
  if (!root) return;

  const KEY = '__fsProductDetailsInstances';
  const instances = window[KEY] || (window[KEY] = new Map());

  const existing = instances.get(sectionId);

  if (existing && existing.cleanup) {
    existing.cleanup();
    instances.delete(sectionId);
  }

  const slider = root.querySelector('[data-fs-product-gallery-slider]');
  const galleryTrack = root.querySelector('[data-fs-product-gallery-track]');
  const galleryDataScript = root.querySelector('[data-fs-product-gallery-data]');
  const galleryNextButton = root.querySelector('.fs-product-gallery__btn-next');
  const pagination = root.querySelector('[data-fs-product-gallery-pagination]');
  const galleryDots = root.querySelector('[data-fs-product-gallery-dots]');
  const stickyBar = root.querySelector('[data-fs-sticky-atc]');
  const atfSentinel = root.querySelector('[data-fs-atf-sentinel]');
  const productFormId = root.getAttribute('data-product-form-id');
  const variantInput = root.querySelector('[data-fs-variant-id-input]');
  const variantButtons = [...root.querySelectorAll('[data-fs-variant-option]')];
  const optionValue = root.querySelector('[data-fs-option-value]');
  const priceEl = root.querySelector('[data-fs-price]');
  const stickyPriceEl = root.querySelector('[data-fs-sticky-price]');
  const descriptionBlock = root.querySelector('[data-fs-description]');
  const descriptionText = root.querySelector('[data-fs-description-text]');
  const descriptionToggle = root.querySelector('[data-fs-description-toggle]');
  const faqParentAccordion = root.querySelector('[data-fs-faq-parent-accordion]');
  const faqItems = [...root.querySelectorAll('[data-fs-faq-item]')];
  const atcButtons = productFormId ? [...root.querySelectorAll(`button[form="${productFormId}"]`)] : [];
  const dispatchCountdown = root.querySelector('[data-fs-dispatch-countdown]');
  const debugDataScript = root.querySelector('[data-fs-pdp-debug]');
  const stickyAtcBodyClass = 'has-fs-sticky-atc-visible';
  const isComingSoon = root.dataset.isComingSoon === 'true';
  const dispatchTooltipText = 'Visit the Shipping page to learn more.';

  let swiper = null;
  let sectionObserver = null;
  let dispatchCountdownTimeout = null;
  let dispatchCountdownInterval = null;
  const handlers = [];
  let galleryData = { fallbackSlides: [], variantSlidesById: {} };
  let galleryDotHandlers = [];
  let debugData = null;

  const atcDefaultLabel = atcButtons[0]?.textContent.trim() ?? '';

  if (debugDataScript) {
    try {
      debugData = JSON.parse(debugDataScript.textContent || '{}');
    } catch (error) {
      console.warn('[fs-product-details] Failed to parse PDP debug data:', error);
    }
  }

  if (galleryDataScript) {
    try {
      const parsedGalleryData = JSON.parse(galleryDataScript.textContent || '{}');
      galleryData = {
        fallbackSlides: Array.isArray(parsedGalleryData?.fallbackSlides) ? parsedGalleryData.fallbackSlides : [],
        variantSlidesById:
          parsedGalleryData?.variantSlidesById && typeof parsedGalleryData.variantSlidesById === 'object'
            ? parsedGalleryData.variantSlidesById
            : {},
      };
    } catch (error) {
      console.warn('[fs-product-details] Failed to parse gallery data:', error);
    }
  }

  const animatePriceChange = (() => {
    const DUR = 450;
    const EASE = 'cubic-bezier(0.87, 0, 0.13, 1)';
    const STAGGER = 25;
    const EMPTY = '\x00';

    const buildStatic = (el, price) => {
      el.innerHTML = '';
      for (const ch of price) {
        const span = document.createElement('span');
        span.className = 'fs-price-slot';
        span.textContent = ch;
        el.appendChild(span);
      }
      el.dataset.price = price;
    };

    return (el, newPrice) => {
      if (!el) return;
      const oldPrice = el.dataset.price;
      if (oldPrice === newPrice) return;
      el.dataset.price = newPrice;

      if (!oldPrice) {
        buildStatic(el, newPrice);
        return;
      }

      el.innerHTML = '';

      // Keep common prefix (e.g. '$') as static slots — never animates
      let prefixLen = 0;
      const minLen = Math.min(oldPrice.length, newPrice.length);
      while (prefixLen < minLen && oldPrice[prefixLen] === newPrice[prefixLen]) {
        prefixLen++;
      }

      for (const ch of newPrice.slice(0, prefixLen)) {
        const slot = document.createElement('span');
        slot.className = 'fs-price-slot';
        slot.textContent = ch;
        el.appendChild(slot);
      }

      // Right-align and animate only the changing numeric part
      const oldSuffix = oldPrice.slice(prefixLen);
      const newSuffix = newPrice.slice(prefixLen);
      const maxLen = Math.max(oldSuffix.length, newSuffix.length);
      const oldChars = oldSuffix.padStart(maxLen, EMPTY).split('');
      const newChars = newSuffix.padStart(maxLen, EMPTY).split('');

      const animated = [];

      for (let i = 0; i < maxLen; i++) {
        const och = oldChars[i] === EMPTY ? null : oldChars[i];
        const nch = newChars[i] === EMPTY ? null : newChars[i];

        if (och === null && nch === null) continue;

        const slot = document.createElement('span');
        slot.className = 'fs-price-slot';

        if (och === nch) {
          slot.textContent = nch;
        } else {
          const track = document.createElement('span');
          track.className = 'fs-price-track';

          const topEl = document.createElement('span');
          topEl.className = 'fs-price-char';
          topEl.textContent = och ?? '\u00A0';

          const botEl = document.createElement('span');
          botEl.className = 'fs-price-char';
          botEl.textContent = nch ?? '\u00A0';

          track.append(topEl, botEl);
          slot.appendChild(track);

          animated.push({ slot, track, isNew: och === null, isRemoved: nch === null, nch, idx: animated.length });
        }

        el.appendChild(slot);
      }

      // Batch all DOM reads before any writes to avoid multiple reflows
      const widths = new Map();
      for (const { slot, isNew, isRemoved } of animated) {
        if (isNew || isRemoved) {
          widths.set(slot, slot.getBoundingClientRect().width);
        }
      }

      // Set initial states (all writes after all reads)
      for (const { slot, isNew, isRemoved } of animated) {
        if (isNew) {
          slot.style.width = '0px';
          slot.style.opacity = '0';
        }
        if (isRemoved) {
          slot.style.width = widths.get(slot) + 'px';
        }
      }

      const total = animated.length;
      const maxDelay = total > 1 ? (total - 1) * STAGGER : 0;

      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          for (const { slot, track, isNew, isRemoved, idx } of animated) {
            // Rightmost animated slot = 0 delay, leftmost = most delay (odometer cascade)
            const delay = (total - 1 - idx) * STAGGER;

            track.style.transition = `transform ${DUR}ms ${EASE} ${delay}ms`;
            track.style.transform = 'translateY(-50%)';

            if (isNew) {
              slot.style.transition = `width ${DUR}ms ${EASE} ${delay}ms, opacity ${Math.round(DUR * 0.45)}ms ease ${delay + Math.round(DUR * 0.15)}ms`;
              slot.style.width = widths.get(slot) + 'px';
              slot.style.opacity = '1';
            }

            if (isRemoved) {
              slot.style.transition = `width ${DUR}ms ${EASE} ${delay}ms, opacity ${Math.round(DUR * 0.3)}ms ease ${delay}ms`;
              slot.style.width = '0px';
              slot.style.opacity = '0';
            }
          }

          setTimeout(
            () => {
              for (const { slot, isRemoved, nch } of animated) {
                if (!slot.isConnected) continue;
                if (isRemoved) {
                  slot.remove();
                } else {
                  slot.style.cssText = '';
                  slot.className = 'fs-price-slot';
                  slot.textContent = nch;
                }
              }
            },
            DUR + maxDelay + 100,
          );
        }),
      );
    };
  })();

  // Initialize price elements immediately so spans are set up before first variant click
  if (priceEl) animatePriceChange(priceEl, priceEl.textContent.trim());
  if (stickyPriceEl) animatePriceChange(stickyPriceEl, stickyPriceEl.textContent.trim());

  const setButtonAvailability = (isAvailable) => {
    atcButtons.forEach((button) => {
      const isDisabled = isComingSoon || !isAvailable;
      button.disabled = isDisabled;
      button.setAttribute('aria-disabled', String(isDisabled));
      button.textContent = isComingSoon ? atcDefaultLabel : isAvailable ? atcDefaultLabel : 'Sold out';
    });

  };

  const initialVariantAvailable = variantButtons.find((button) => button.classList.contains('is-active'))?.dataset.variantAvailable !== 'false';
  setButtonAvailability(initialVariantAvailable);

  const setupImageFadeIn = (image) => {
    if (!image) return;

    const revealImage = () => {
      image.classList.remove('is-loading');
      image.removeEventListener('load', revealImage);
      image.removeEventListener('error', revealImage);
    };

    if (image.complete && image.naturalWidth > 0) {
      revealImage();
      return;
    }

    image.addEventListener('load', revealImage, { once: true });
    image.addEventListener('error', revealImage, { once: true });

    handlers.push(() => {
      image.removeEventListener('load', revealImage);
      image.removeEventListener('error', revealImage);
    });
  };

  const setupGalleryImageFadeIns = () => {
    root.querySelectorAll('.fs-product-gallery__image').forEach(setupImageFadeIn);
  };

  setupGalleryImageFadeIns();

  const setDispatchCountdownVisibility = (visible) => {
    if (!dispatchCountdown) return;
    dispatchCountdown.hidden = !visible;
  };

  const dispatchTimeTokenPattern = /\{\{\s*time\s*\}\}/i;

  const appendDispatchText = (container, text) => {
    const value = String(text ?? '');
    const hasAsterisk = value.endsWith('*');
    const textValue = hasAsterisk ? value.slice(0, -1) : value;

    if (textValue) {
      container.appendChild(document.createTextNode(textValue));
    }

    if (!hasAsterisk) return;

    const asterisk = document.createElement('span');
    asterisk.className = 'fs-product-details__dispatch-asterisk';
    asterisk.dataset.tooltip = dispatchTooltipText;
    asterisk.setAttribute('aria-label', dispatchTooltipText);
    asterisk.setAttribute('tabindex', '0');
    asterisk.textContent = '*';
    container.appendChild(asterisk);
  };

  const setDispatchCountdownContent = (partsList) => {
    if (!dispatchCountdown) return;

    dispatchCountdown.textContent = '';

    partsList.forEach((part, index) => {
      if (index > 0) {
        dispatchCountdown.appendChild(document.createTextNode(' '));
      }

      if (part.type === 'time') {
        const time = document.createElement('span');
        time.className = 'fs-product-details__dispatch-countdown-time';
        time.textContent = part.value;
        dispatchCountdown.appendChild(time);
        return;
      }

      appendDispatchText(dispatchCountdown, part.value);
    });
  };

  const formatDispatchTimeRemaining = (totalMinutes) => {
    const safeMinutes = Math.max(0, totalMinutes);
    const hours = Math.floor(safeMinutes / 60);
    const minutes = safeMinutes % 60;
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const getDispatchOverride = () => String(dispatchCountdown?.dataset.dispatchOverride || '').trim();

  const renderDispatchOverride = (overrideText, timeRemaining = '') => {
    const overrideValue = String(overrideText ?? '').trim();
    if (!overrideValue) return false;

    if (!dispatchTimeTokenPattern.test(overrideValue)) {
      setDispatchCountdownContent([{ type: 'text', value: overrideValue }]);
      return true;
    }

    if (!timeRemaining) return false;

    const partsList = [];
    const segments = overrideValue.split(dispatchTimeTokenPattern);

    segments.forEach((segment, index) => {
      const normalizedSegment = segment.trim();
      if (normalizedSegment) {
        partsList.push({ type: 'text', value: normalizedSegment });
      }

      if (index < segments.length - 1) {
        partsList.push({ type: 'time', value: timeRemaining });
      }
    });

    setDispatchCountdownContent(partsList);
    return true;
  };

  const updateDispatchCountdown = () => {
    if (!dispatchCountdown) return;

    const timeZone = root.dataset.dispatchTimezone || 'America/New_York';
    const cutoffHourAttr = Number.parseInt(root.dataset.nextDayDispatchCutoffHour || '', 10);
    const cutoffHour = Number.isFinite(cutoffHourAttr) ? cutoffHourAttr : 17;
    const prefix = dispatchCountdown.dataset.dispatchPrefix || 'Order within';
    const nextDaySuffix = dispatchCountdown.dataset.dispatchSuffix || 'for next-day dispatch*';
    const mondaySuffix = dispatchCountdown.dataset.dispatchMondaySuffix || 'for Monday dispatch*';
    const mondayStaticLabel = dispatchCountdown.dataset.dispatchWeekendLabel || 'Order now for Monday dispatch*';
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    }).formatToParts(now);

    const getPart = (type) => parts.find((part) => part.type === type)?.value || '';
    const weekday = getPart('weekday');
    const hour = Number.parseInt(getPart('hour'), 10);
    const minute = Number.parseInt(getPart('minute'), 10);

    if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
      setDispatchCountdownVisibility(false);
      return;
    }

    const cutoffMinutes = cutoffHour * 60;
    const localMinutes = hour * 60 + minute;
    const isFriday = weekday === 'Fri';
    const isSaturday = weekday === 'Sat';
    const isSunday = weekday === 'Sun';
    const diff = cutoffMinutes - localMinutes;
    const timeRemaining = diff > 0 ? formatDispatchTimeRemaining(diff) : '';
    const dispatchOverride = getDispatchOverride();

    if (dispatchOverride) {
      const renderedOverride = renderDispatchOverride(dispatchOverride, timeRemaining);
      setDispatchCountdownVisibility(renderedOverride);
      return;
    }

    if ((isFriday && localMinutes >= cutoffMinutes) || isSaturday || isSunday) {
      setDispatchCountdownContent([{ type: 'text', value: mondayStaticLabel }]);
      setDispatchCountdownVisibility(true);
      return;
    }

    if (diff <= 0) {
      setDispatchCountdownVisibility(false);
      return;
    }

    const suffix = isFriday ? mondaySuffix : nextDaySuffix;

    setDispatchCountdownContent([
      { type: 'text', value: prefix },
      { type: 'time', value: timeRemaining },
      { type: 'text', value: suffix },
    ]);
    setDispatchCountdownVisibility(true);
  };

  if (dispatchCountdown) {
    updateDispatchCountdown();

    const msUntilNextMinute = 60000 - (Date.now() % 60000);

    dispatchCountdownTimeout = window.setTimeout(() => {
      updateDispatchCountdown();
      dispatchCountdownInterval = window.setInterval(updateDispatchCountdown, 60000);
    }, msUntilNextMinute);

    handlers.push(() => {
      if (dispatchCountdownTimeout) {
        window.clearTimeout(dispatchCountdownTimeout);
      }
      if (dispatchCountdownInterval) {
        window.clearInterval(dispatchCountdownInterval);
      }
    });
  }

  const destroyGallerySwiper = () => {
    if (swiper && !swiper.destroyed) {
      swiper.destroy(true, true);
    }
    swiper = null;
  };

  const cleanupGalleryDots = () => {
    galleryDotHandlers.forEach((dispose) => dispose());
    galleryDotHandlers = [];
    if (galleryDots) {
      galleryDots.replaceChildren();
    }
  };

  const setActiveGalleryDot = (index) => {
    if (!galleryDots) return;
    const dots = galleryDots.querySelectorAll('.pdp-gallery__dot');
    dots.forEach((dot, dotIndex) => {
      dot.classList.toggle('pdp-gallery__dot--active', dotIndex === index);
      dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
    });
  };

  const renderGalleryDots = (slideCount, initialIndex = 0) => {
    cleanupGalleryDots();
    if (!galleryDots) return;

    if (!Number.isFinite(slideCount) || slideCount <= 1) {
      galleryDots.hidden = true;
      return;
    }

    galleryDots.hidden = false;

    for (let index = 0; index < slideCount; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'pdp-gallery__dot';
      dot.setAttribute('aria-label', `Go to gallery image ${index + 1}`);
      dot.setAttribute('aria-current', 'false');

      const onClick = () => {
        if (!swiper || swiper.destroyed) return;
        if (typeof swiper.slideToLoop === 'function') {
          swiper.slideToLoop(index);
        } else {
          swiper.slideTo(index);
        }
      };

      dot.addEventListener('click', onClick);
      galleryDotHandlers.push(() => dot.removeEventListener('click', onClick));
      galleryDots.appendChild(dot);
    }

    setActiveGalleryDot(initialIndex);
  };

  const initGallerySwiper = (initialSlide = 0) => {
    if (!window.Swiper || !slider || !galleryTrack) return;

    const slideCount = galleryTrack.querySelectorAll('.swiper-slide').length;
    if (slideCount === 0) return;

    const normalizedInitialSlide =
      slideCount > 0 ? Math.max(0, Math.min(initialSlide, slideCount - 1)) : 0;

    swiper = new window.Swiper(slider, {
      initialSlide: normalizedInitialSlide,
      speed: 700,
      loop: slideCount > 1,
      watchOverflow: true,
      pagination: {
        el: pagination,
        clickable: true,
      },
      breakpoints: {
        0: {
          direction: 'horizontal',
          spaceBetween: 20,
          pagination: {
            enabled: true,
          },
          mousewheel: {
            enabled: false,
          },
        },
        769: {
          direction: 'vertical',
          spaceBetween: 120,
          navigation: {
            nextEl: galleryNextButton,
          },
          pagination: {
            enabled: false,
          },
          mousewheel: {
            enabled: true,
            forceToAxis: true,
            releaseOnEdges: false,
            sensitivity: 0.7,
          },
        },
      },
    });

    setActiveGalleryDot(swiper.realIndex || 0);
    swiper.on('slideChange', () => setActiveGalleryDot(swiper.realIndex || 0));
  };

  const createGallerySlide = (slideData, index) => {
    if (!slideData || !slideData.src) return null;

    const slide = document.createElement('div');
    const fitMode = slideData.fitMode === 'cover' ? 'cover' : 'contain';
    slide.className = `fs-product-gallery__slide swiper-slide${fitMode === 'cover' ? ' fs-product-gallery__slide--desktop-cover' : ''}`;

    if (slideData.id) {
      slide.dataset.mediaId = slideData.id;
    }
    slide.dataset.fitMode = fitMode;

    const slideImage = document.createElement('div');
    slideImage.className = 'fs-product-gallery__slide-image';
    slideImage.style.setProperty('--ratio', String(slideData.ratio || 1));

    const image = document.createElement('img');
    image.className = 'fs-product-gallery__image is-loading';
    image.src = slideData.src;
    image.alt = slideData.alt || '';
    image.decoding = 'async';
    image.loading = index === 0 ? 'eager' : 'lazy';

    if (slideData.srcset) {
      image.srcset = slideData.srcset;
    }

    slideImage.appendChild(image);
    slide.appendChild(slideImage);
    return slide;
  };

  const renderGallerySlides = (slides, initialSlide = 0) => {
    if (!galleryTrack) return;

    destroyGallerySwiper();

    const slideElements = (Array.isArray(slides) ? slides : [])
      .map((slide, index) => createGallerySlide(slide, index))
      .filter(Boolean);

    galleryTrack.replaceChildren(...slideElements);
    renderGalleryDots(slideElements.length, initialSlide);
    setupGalleryImageFadeIns();
    initGallerySwiper(initialSlide);
  };

  const getGallerySlidesForVariant = (variantId) => {
    const variantSlides = galleryData.variantSlidesById?.[String(variantId)];
    if (Array.isArray(variantSlides) && variantSlides.length > 0) {
      return variantSlides;
    }

    return Array.isArray(galleryData.fallbackSlides) ? galleryData.fallbackSlides : [];
  };

  const renderGalleryForVariant = (variantId, initialSlide = 0) => {
    renderGallerySlides(getGallerySlidesForVariant(variantId), initialSlide);
  };

  const setActiveVariant = (button) => {
    if (!button || !variantInput) return;

    const variantId = button.dataset.variantId;
    const variantName = button.dataset.variantName || '';
    const variantAvailable = button.dataset.variantAvailable === 'true';
    const variantPrice = button.dataset.variantPrice || '';
    const variantGalleryInitialIndex = Number.parseInt(button.dataset.variantGalleryInitialIndex || '0', 10);
    const normalizedVariantGalleryInitialIndex = Number.isFinite(variantGalleryInitialIndex)
      ? variantGalleryInitialIndex
      : 0;

    variantInput.value = variantId;

    if (optionValue) {
      optionValue.textContent = variantName;
    }
    animatePriceChange(priceEl, variantPrice);
    animatePriceChange(stickyPriceEl, variantPrice);

    variantButtons.forEach((item) => {
      const isActive = item === button;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-selected', String(isActive));
    });

    if (dispatchCountdown) {
      dispatchCountdown.dataset.dispatchOverride = button.dataset.variantDispatchLine || '';
      updateDispatchCountdown();
    }

    setButtonAvailability(variantAvailable);
    renderGalleryForVariant(variantId, normalizedVariantGalleryInitialIndex);

    if (debugData) {
      console.info(`[fs-product-details][${sectionId}] Variant selected`, {
        variantId,
        variantName,
        variantAvailable,
        variantPrice,
        buttonLabels: atcButtons.map((item) => item.textContent.trim()),
      });
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    history.replaceState(null, '', url.toString());
  };

  variantButtons.forEach((button) => {
    const onClick = () => setActiveVariant(button);
    button.addEventListener('click', onClick);
    handlers.push(() => button.removeEventListener('click', onClick));
  });

  // ---- AJAX Add to Cart ----
  const form = productFormId ? document.getElementById(productFormId) : null;

  const setLoadingState = (loading) => {
    atcButtons.forEach((btn) => {
      btn.classList.toggle('is-loading', loading);
      btn.setAttribute('aria-busy', loading ? 'true' : 'false');
    });
  };

  if (form) {
    const onFormSubmit = async (event) => {
      event.preventDefault();

      if (isComingSoon) return;

      const variantId = variantInput ? parseInt(variantInput.value, 10) : null;
      if (!variantId) return;

      setLoadingState(true);
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => null);
          console.warn('[fs-product-details] Add to cart failed:', err?.description || res.status);
          return;
        }

        const item = await res.json();
        document.dispatchEvent(new CustomEvent('fs:cart:add', { detail: { item } }));
      } catch (err) {
        console.warn('[fs-product-details] Add to cart error:', err);
      } finally {
        setLoadingState(false);
      }
    };

    form.addEventListener('submit', onFormSubmit);
    handlers.push(() => form.removeEventListener('submit', onFormSubmit));
  }

  if (faqParentAccordion) {
    const faqSummary = faqParentAccordion.querySelector('.fs-product-faq-accordion__summary');

    const setExpanded = (expanded) => {
      faqParentAccordion.classList.toggle('is-open', expanded);
      if (faqSummary) faqSummary.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    };

    setExpanded(false);

    if (faqSummary) {
      const onFaqToggle = (event) => {
        event.preventDefault();
        setExpanded(!faqParentAccordion.classList.contains('is-open'));
      };

      const onFaqKeydown = (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        onFaqToggle(event);
      };

      faqSummary.addEventListener('click', onFaqToggle);
      faqSummary.addEventListener('keydown', onFaqKeydown);

      handlers.push(() => faqSummary.removeEventListener('click', onFaqToggle));
      handlers.push(() => faqSummary.removeEventListener('keydown', onFaqKeydown));
    }
  }

  faqItems.forEach((item) => {
    const summary = item.querySelector('.fs-product-details__accordion-summary');
    if (!summary) return;

    const setExpanded = (expanded) => {
      item.classList.toggle('is-open', expanded);
      summary.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    };

    setExpanded(item.classList.contains('is-open'));

    const onToggle = (event) => {
      event.preventDefault();
      const willOpen = !item.classList.contains('is-open');

      if (willOpen) {
        faqItems.forEach((otherItem) => {
          if (otherItem === item) return;
          const otherSummary = otherItem.querySelector('.fs-product-details__accordion-summary');
          if (!otherSummary) return;
          otherItem.classList.remove('is-open');
          otherSummary.setAttribute('aria-expanded', 'false');
        });
      }

      setExpanded(willOpen);
    };

    const onKeydown = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      onToggle(event);
    };

    summary.addEventListener('click', onToggle);
    summary.addEventListener('keydown', onKeydown);
    handlers.push(() => summary.removeEventListener('click', onToggle));
    handlers.push(() => summary.removeEventListener('keydown', onKeydown));
  });

  if (descriptionToggle && descriptionText && descriptionBlock) {
    const updateDescriptionToggle = () => {
      const isExpanded = descriptionToggle.getAttribute('aria-expanded') === 'true';
      if (isExpanded) {
        descriptionToggle.hidden = false;
        return;
      }
      const isOverflowing = descriptionText.scrollHeight > descriptionText.clientHeight + 1;
      descriptionToggle.hidden = !isOverflowing;
    };

    const onDescriptionToggle = () => {
      const isExpanded = descriptionToggle.getAttribute('aria-expanded') === 'true';
      const nextExpanded = !isExpanded;
      descriptionToggle.setAttribute('aria-expanded', String(nextExpanded));
      descriptionToggle.textContent = isExpanded ? 'More..' : 'Show less';
      descriptionText.classList.toggle('is-collapsed', isExpanded);
      descriptionBlock.classList.toggle('is-expanded', nextExpanded);
      requestAnimationFrame(updateDescriptionToggle);
    };

    updateDescriptionToggle();
    window.addEventListener('resize', updateDescriptionToggle);
    descriptionToggle.addEventListener('click', onDescriptionToggle);
    handlers.push(() => window.removeEventListener('resize', updateDescriptionToggle));
    handlers.push(() => descriptionToggle.removeEventListener('click', onDescriptionToggle));
  }

  // Activate the initially selected variant (from ?variant= URL param or default)
  const initialActiveBtn = variantButtons.find((btn) => btn.classList.contains('is-active'));
  const hasPreloadedGalleryData =
    (Array.isArray(galleryData.fallbackSlides) && galleryData.fallbackSlides.length > 0) ||
    Object.values(galleryData.variantSlidesById || {}).some((slides) => Array.isArray(slides) && slides.length > 0);

  if (slider) {
    if (hasPreloadedGalleryData && initialActiveBtn?.dataset.variantId) {
      const initialVariantGalleryIndex = Number.parseInt(initialActiveBtn.dataset.variantGalleryInitialIndex || '0', 10);
      renderGalleryForVariant(
        initialActiveBtn.dataset.variantId,
        Number.isFinite(initialVariantGalleryIndex) ? initialVariantGalleryIndex : 0,
      );
    } else {
      initGallerySwiper();
    }

    const onWheel = (event) => {
      if (window.innerWidth < 990) return;
      event.preventDefault();
    };

    slider.addEventListener('wheel', onWheel, { passive: false });
    handlers.push(() => slider.removeEventListener('wheel', onWheel));
  }

  if (stickyBar && atfSentinel && 'IntersectionObserver' in window) {
    sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isVisible = !entry.isIntersecting;
          stickyBar.classList.toggle('is-visible', isVisible);
          document.body.classList.toggle(stickyAtcBodyClass, isVisible);
        });
      },
      {
        root: null,
        rootMargin: '-40px 0px 0px 0px',
        threshold: 0.01,
      },
    );

    sectionObserver.observe(atfSentinel);
  }

  const cleanup = () => {
    handlers.forEach((dispose) => dispose());

    document.body.classList.remove(stickyAtcBodyClass);

    if (sectionObserver) {
      sectionObserver.disconnect();
    }

    cleanupGalleryDots();
    destroyGallerySwiper();
  };

  instances.set(sectionId, { cleanup });
  return true;
};

document.addEventListener('shopify:section:load', (event) => {
  const loaded =
    (event.target.matches && event.target.matches('[data-fs-product-details]') && event.target) ||
    event.target.querySelector('[data-fs-product-details]');
  const loadedSectionId = loaded && loaded.getAttribute('data-section-id');
  if (!loadedSectionId) return;
  initProductDetails(loadedSectionId);
});

document.addEventListener('shopify:section:unload', (event) => {
  const unloaded =
    (event.target.matches && event.target.matches('[data-fs-product-details]') && event.target) ||
    event.target.querySelector('[data-fs-product-details]');
  const unloadedSectionId = unloaded && unloaded.getAttribute('data-section-id');
  if (!unloadedSectionId) return;

  const instances = window.__fsProductDetailsInstances;
  const instance = instances && instances.get(unloadedSectionId);

  if (!instance || !instance.cleanup) return;
  instance.cleanup();
  instances.delete(unloadedSectionId);
});
